import axios from 'axios';
import { TrackingHistoryDto } from '@/features/map/map.type';
import { GetThermometersParams, ThermometerDto, ThermometerUpdateDto } from '@/features/sensors/dtos/thermometer.dto';
import { SpeedLimitService } from '@/OptimusRoutes/constants';
import { ServiceResponse } from '../../OptimusRoutes/interfaces';
import { MultimediaDto, MultimediaEventDto } from '../multimedia/multimedia.dto';
import { GetTrailHistoryParams, UploadDeviceFile, UploadDevicesResponse } from './device.types';

type Service = {
	getMultimediaVideo: (id: number, startDate: string, endDate: string) => Promise<ServiceResponse<MultimediaDto[]>>;
	getEventsMultimedia: (
		id: number,
		startDate: string,
		endDate: string
	) => Promise<ServiceResponse<MultimediaEventDto[]>>;
	getEventsMultimediaByTrackingHistoryId: (
		id: number,
		trackingHistoryId: string
	) => Promise<ServiceResponse<MultimediaEventDto[]>>;
	getTrailHistory: (args: GetTrailHistoryParams) => Promise<ServiceResponse<TrackingHistoryDto[]>>;
	updateDescription: (id: number, description: string) => Promise<ServiceResponse<void>>;
	uploadFile: (data: UploadDeviceFile) => Promise<ServiceResponse<UploadDevicesResponse>>;
	getThermometers: (params: GetThermometersParams) => Promise<ServiceResponse<ThermometerDto[]>>;
	createThermometer: (id: number) => Promise<ServiceResponse<ThermometerDto>>;
	updateThermometer: (
		id: number,
		thermometerId: number,
		update: ThermometerUpdateDto
	) => Promise<ServiceResponse<ThermometerDto>>;
	changePostedSpeedLimitStatus: (deviceId: number, speedLimitService: SpeedLimitService) => Promise<ServiceResponse>;
	deleteDeviceTrackingHistory: (id: number) => Promise<ServiceResponse>;
	deletePositionById: (deviceId: number, trackingId: string) => Promise<ServiceResponse>;
};

const getEventsMultimedia = async (
	id: number,
	startDate: string,
	endDate: string
): Promise<ServiceResponse<MultimediaEventDto[]>> => {
	let options = {
		params: {
			'start-date': startDate,
			'end-date': endDate,
		},
	};

	const { data } = await axios.get<ServiceResponse<MultimediaEventDto[]>>(
		`/v2/devices/${id}/multimedia-events`,
		options
	);
	return data;
};

const getMultimediaVideo = async (
	id: number,
	startDate: string,
	endDate: string
): Promise<ServiceResponse<MultimediaDto[]>> => {
	let options = {
		params: {
			'start-date': startDate,
			'end-date': endDate,
		},
	};

	const { data } = await axios.get<ServiceResponse<MultimediaDto[]>>(`/v2/devices/${id}/multimedia-videos`, options);
	return data;
};

const getTrailHistory = async ({
	id,
	startDate,
	endDate,
	cancelToken,
}: GetTrailHistoryParams): Promise<ServiceResponse<TrackingHistoryDto[]>> => {
	const params = {
		'start-date': startDate,
		'end-date': endDate,
	};

	const { data } = await axios.get<ServiceResponse<TrackingHistoryDto[]>>(`/v2/devices/${id}/trail-history`, {
		params,
		cancelToken,
	});
	return data;
};

const getEventsMultimediaByTrackingHistoryId = async (
	id: number,
	trackingHistoryId: string
): Promise<ServiceResponse<MultimediaEventDto[]>> => {
	const { data } = await axios.get<ServiceResponse<MultimediaEventDto[]>>(
		`/v2/devices/${id}/multimedia-events/${trackingHistoryId}`
	);
	return data;
};

const uploadFile = async (values: UploadDeviceFile): Promise<ServiceResponse<UploadDevicesResponse>> => {
	const formData = new FormData();
	(Object.keys(values) as (keyof UploadDeviceFile)[]).forEach((key) => {
		const value = values[key];
		if (key === 'file') {
			formData.append('file', values.file);
			return;
		}
		if (value !== undefined && value !== null) {
			formData.append(key, value.toString());
		}
	});
	const { data } = await axios.post<ServiceResponse<UploadDevicesResponse>>('/v2/devices/upload-file', formData, {
		headers: { 'Content-Type': 'multipart/form-data' },
	});
	return data;
};

const updateDescription = async (id: number, description: string): Promise<ServiceResponse<void>> => {
	const { data } = await axios.patch(`/v2/devices/${id}/description`, { description });
	return data;
};

const changePostedSpeedLimitStatus = async (
	deviceId: number,
	speedLimitService: SpeedLimitService
): Promise<ServiceResponse> => {
	const { data } = await axios.post<ServiceResponse>(`/v2/devices/${deviceId}/posted-speed-limit`, {
		speedLimitService,
	});
	return data;
};

const getThermometers = async ({
	id,
	includeAlertConfig,
	status,
}: GetThermometersParams): Promise<ServiceResponse<ThermometerDto[]>> => {
	const { data } = await axios.get<ServiceResponse<ThermometerDto[]>>(`/v2/devices/${id}/thermometers`, {
		params: { includeAlertConfig, status },
	});
	return data;
};

const createThermometer = async (id: number): Promise<ServiceResponse<ThermometerDto>> => {
	const { data } = await axios.post<ServiceResponse<ThermometerDto>>(`/v2/devices/${id}/thermometers`);
	return data;
};
const updateThermometer = async (
	id: number,
	thermometerId: number,
	update: ThermometerUpdateDto
): Promise<ServiceResponse<ThermometerDto>> => {
	const { data } = await axios.patch<ServiceResponse<ThermometerDto>>(
		`/v2/devices/${id}/thermometers/${thermometerId}`,
		update
	);
	return data;
};

const deleteDeviceTrackingHistory = async (id: number): Promise<ServiceResponse> => {
	const { data } = await axios.delete<ServiceResponse>(`/v2/devices/${id}/tracking-history`);
	return data;
};

const deletePositionById = async (deviceId: number, trackingId: string): Promise<ServiceResponse> => {
	const { data } = await axios.delete<ServiceResponse>(`/v2/devices/${deviceId}/positions/${trackingId}`);
	return data;
};

const DeviceService: Service = {
	getMultimediaVideo,
	getEventsMultimedia,
	getEventsMultimediaByTrackingHistoryId,
	uploadFile,
	updateDescription,
	changePostedSpeedLimitStatus,
	getThermometers,
	createThermometer,
	updateThermometer,
	deleteDeviceTrackingHistory,
	deletePositionById,
	getTrailHistory,
};

export default DeviceService;
